import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import type { DateValueType } from "react-tailwindcss-datepicker";
import AttachTaskSection from "@/components/AttachTaskSection";
import Autocomplete from "@/components/Autocomplete";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import Chip, { ChipSet } from "@/components/Chip";
import Icon from "@/components/Icon";
import ListItem from "@/components/ListItem/ListItem";
import Progress from "@/components/Progress";
import SideSheetModal from "@/components/SideSheetModal";
import { SkeletonElement } from "@/components/Skeleton";
import SubSectionHeading from "@/components/SubSectionHeading/SubSectionHeading";
import TextField from "@/components/TextField";
import { INTERACTION_ANALYTICS_EVENTS, INTERACTION_CREATE_SOURCES } from "@/components/interactions/amplitude";
import { refetchPersonInteractionViews } from "@/components/interactions/refetchPersonInteractionViews";
import {
  INTERACTION_CATEGORIES_METADATA_QUERY,
  type InteractionCategoriesMetadataData
} from "@/components/interactions/queries";
import { AlgoliaSearchResults, getPersonDisplayName, type PersonHit } from "@/components/AlgoliaSearch";
import { renderCreateInteractionPersonHit } from "@/layouts/AppLayout/CreateGlobalInteraction/createInteractionFuzzyHitRenderer";
import { useCreateInteractionFuzzySearch } from "@/layouts/AppLayout/CreateGlobalInteraction/useCreateInteractionFuzzySearch";
import { useCanManageTasksBool } from "@/hooks/useCapabilities";
import useAmplitude from "@/hooks/useAmplitude";
import useAttachTask from "@/hooks/useAttachTask";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useNavigate, type Path } from "@/router";
import {
  ACTIVE_CALL_SESSION_CHANGED_EVENT,
  clearActiveCallSelectionForCallSid,
  readActiveCallDraft,
  readActiveCallSelection,
  storeActiveCallDraft,
  storeActiveCallSelection,
  type ActiveCallDraft,
  type ActiveCallSelection
} from "./sessionStorage";
import { ACTIVE_CALL_FINALIZE_INTERACTION_MUTATION, ACTIVE_CALL_SELECTED_PERSON_QUERY } from "./queries";
import { CALL_TOPIC_TAXONOMY_QUERY, type CallTopicTaxonomyResponse } from "./queries";
import ProcessStepLineItem from "@/components/ProcessStepLineItem";
import useActiveCallTranscriptPoll from "./useActiveCallTranscriptPoll";
import {
  getFinalizeDescription,
  resolveCallTranscriptSummaryStep,
  type TranscriptStatusStep
} from "./transcriptPollUtils";

type Props = {
  enabled: boolean;
};

const dateValueToISODate = (value: DateValueType): string | null => {
  if (!value || typeof value === "string" || !value.startDate) return null;

  const date = value.startDate instanceof Date ? value.startDate : new Date(value.startDate);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().split("T")[0];
};

const isoDateToDateValue = (value?: string | null): DateValueType => {
  if (!value) return null;

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;

  return { startDate: date, endDate: date };
};

const getDefaultTaskName = (personName?: string) => {
  return personName ? `Follow-up to call with ${personName}` : "Follow-up to active call";
};

const getTabLabel = (personName?: string) => {
  if (personName) return `Call Details: ${personName.split(" ")[0]}`;
  return "Call Details";
};

const FINALIZE_INTERACTION_ERROR_MESSAGE = "There was an error finalizing the interaction. Please try again later.";
const LOAD_INTERACTION_TOPICS_ERROR_MESSAGE = "We couldn't load interaction topics. Please try again.";

type TopicOption = {
  topic: string;
  subtopic: string;
  label: string;
};

const topicOptionLabel = (topic: string, subtopic: string) => `${topic} › ${subtopic}`;

// Display label for a topic pair, defaulting missing halves to "Other".
// Shared by prefill, select, reset, and blur so the input always shows the effective pair.
const topicPairLabel = (topic: string | null | undefined, subtopic: string | null | undefined) =>
  topicOptionLabel(topic ?? "Other", subtopic ?? "Other");

export default function ActiveCallSideSheet({ enabled }: Props) {
  const location = useLocation();
  const currentUser = useCurrentBackofficeUser();
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const canManageTasks = useCanManageTasksBool();
  const showAttachTask = canManageTasks;
  const taskHook = useAttachTask(currentUser);
  const {
    attachTask,
    clearAssignee,
    clearError,
    reset: resetTask,
    selectAssignee,
    setTaskAssigneeSearch,
    setTaskDueDate,
    setTaskIsPrivate,
    setTaskName,
    taskAssignee,
    taskAssigneeSearch,
    taskDueDate,
    taskErrors,
    taskIsPrivate,
    taskName,
    toggleAttachTask,
    usersData,
    usersLoading
  } = taskHook;
  const [createInteraction, { loading: creatingInteraction }] = useMutation(ACTIVE_CALL_FINALIZE_INTERACTION_MUTATION);
  const [selection, setSelection] = useState<ActiveCallSelection | null>(() =>
    enabled ? readActiveCallSelection() : null
  );
  const [isOpen, setIsOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [topicOverride, setTopicOverride] = useState<string | null>(null);
  const [subtopicOverride, setSubtopicOverride] = useState<string | null>(null);
  const [topicTouched, setTopicTouched] = useState(false);
  const [topicQuery, setTopicQuery] = useState("");
  const appliedTopicCallSidRef = useRef<string>();
  // Latest topic state for the delayed blur restore: Autocomplete fires onBlur
  // 200ms after blur, so the blur-time closure may predate a chip or reset
  // click in the same window.
  const topicBlurStateRef = useRef({ touched: false, pending: true, topic: "Other", subtopic: "Other" });
  const [finalizeError, setFinalizeError] = useState<string>();
  const [isChangingPerson, setIsChangingPerson] = useState(false);
  const hydratedCallSidRef = useRef<string>();
  const notesInputRef = useRef<HTMLInputElement>(null);
  const shouldFocusNotesAfterSelectionRef = useRef(false);
  const shouldFocusSearchAfterClearRef = useRef(false);
  const { data: selectedPersonData } = useQuery(ACTIVE_CALL_SELECTED_PERSON_QUERY, {
    variables: { pgaId: selection?.pgaId || "" },
    skip: !selection?.pgaId
  });
  const selectedPerson = selectedPersonData?.person;
  const selectedPersonName = selectedPerson?.displayName;
  const hasSelectedPerson = !!selection?.pgaId;
  const isCallEnded = !!selection?.ended;
  const {
    callSid: polledCallSid,
    transcriptSummaryStatus,
    transcriptTopic,
    transcriptSubtopic,
    runnersUp = [],
    interactionId,
    loading: transcriptStatusLoading,
    isPolling: transcriptStatusPolling
  } = useActiveCallTranscriptPoll({
    enabled,
    callEnded: isCallEnded,
    callSid: selection?.callSid
  });
  // Event Streams can lag hangup by a couple of seconds. Finalize 404s without
  // the twilio_flex_calls row, so wait for the poll match rather than Flex wrap-up.
  // A poll query error is already terminal (failed, no SID); still allow save with notes.
  const callRecordReady = !!polledCallSid;
  const canFinalizeInteraction = hasSelectedPerson && (callRecordReady || transcriptSummaryStatus === "failed");
  const showPersonSearch = isChangingPerson || !selection?.pgaId;
  const { data: interactionCategoriesData } = useQuery<InteractionCategoriesMetadataData>(
    INTERACTION_CATEGORIES_METADATA_QUERY,
    {
      fetchPolicy: "cache-first",
      skip: !enabled
    }
  );
  const {
    data: taxonomyData,
    loading: taxonomyLoading,
    error: taxonomyError
  } = useQuery<CallTopicTaxonomyResponse>(CALL_TOPIC_TAXONOMY_QUERY, {
    fetchPolicy: "cache-first",
    skip: !isCallEnded
  });
  const taxonomy = useMemo(() => taxonomyData?.callTopicTaxonomy ?? [], [taxonomyData?.callTopicTaxonomy]);
  const finalizingInteraction = creatingInteraction;
  const validCategoryNames = useMemo(
    () => interactionCategoriesData?.interactionCategories.map((category) => category.name) ?? [],
    [interactionCategoriesData?.interactionCategories]
  );
  const selectedTopic = topicOverride ?? transcriptTopic ?? "Other";
  const selectedSubtopic = subtopicOverride ?? transcriptSubtopic ?? "Other";
  const aiTopicPending = transcriptSummaryStatus !== "completed";

  useEffect(() => {
    topicBlurStateRef.current = {
      touched: topicTouched,
      pending: aiTopicPending,
      topic: selectedTopic,
      subtopic: selectedSubtopic
    };
  }, [topicTouched, aiTopicPending, selectedTopic, selectedSubtopic]);
  const topicComboOptions = useMemo<TopicOption[]>(
    () =>
      taxonomy.flatMap((entry) =>
        (entry.subtopics ?? []).map((subtopic) => ({
          topic: entry.topic,
          subtopic,
          label: topicOptionLabel(entry.topic, subtopic)
        }))
      ),
    [taxonomy]
  );
  const filteredTopicOptions = useMemo(() => {
    const normalized = topicQuery.trim().toLowerCase();
    if (!normalized) return topicComboOptions;
    return topicComboOptions.filter((option) => {
      const topic = option.topic.toLowerCase();
      const subtopic = option.subtopic.toLowerCase();
      return (
        topic.includes(normalized) || subtopic.includes(normalized) || option.label.toLowerCase().includes(normalized)
      );
    });
  }, [topicComboOptions, topicQuery]);
  const visibleRunnersUp = runnersUp.slice(0, 2);
  // An empty taxonomy is as unusable as a failed load: surface the same banner.
  const taxonomyFailed = !!taxonomyError || (!taxonomyLoading && taxonomy.length === 0);
  const aiTopicPlaceholder =
    transcriptSummaryStatus === "failed" || transcriptSummaryStatus === "unavailable" ? "unavailable" : "pending...";
  // Completed classifications with a null half render Other to match the prefill and payload.
  const aiTopicLabel = transcriptTopic ?? (aiTopicPending ? aiTopicPlaceholder : "Other");
  const aiSubtopicLabel = transcriptSubtopic ?? (aiTopicPending ? aiTopicPlaceholder : "Other");
  // Untouched + AI pending sends no topic args so late AI results still land on the call.
  // A touched override was valid when chosen, so current taxonomy availability never gates it.
  const shouldSendTopicArgs = transcriptSummaryStatus === "completed" || topicTouched;
  const finalizeTopicArgs = shouldSendTopicArgs
    ? { twilioFlexTopic: selectedTopic, twilioFlexSubtopic: selectedSubtopic }
    : {};
  const transcriptSummaryStep = isCallEnded
    ? resolveCallTranscriptSummaryStep(transcriptSummaryStatus, {
        loading: transcriptStatusLoading || transcriptStatusPolling,
        callRecordReady
      })
    : null;
  const inCallTranscriptRecordingStep: TranscriptStatusStep | null = !isCallEnded
    ? { status: "processing", headline: "Recording call transcript..." }
    : null;
  const transcriptStatusLineItem = inCallTranscriptRecordingStep ?? transcriptSummaryStep;

  const {
    query,
    displayValue,
    results,
    isLoading,
    highlightedIndex,
    setHighlightedIndex,
    handleQueryChange,
    handleClear,
    handleItemSelected,
    setIsFocused,
    setDisplayValue,
    focusInput,
    itemRefs,
    resultsContainerRef,
    searchBoxRef,
    failed: personSearchFailed
  } = useCreateInteractionFuzzySearch({
    entity: "people",
    hitsPerPage: 10,
    skipListQuery: !enabled,
    getDisplayValue: getPersonDisplayName,
    onSelect: (hit: PersonHit) => {
      if (!selection) return;

      const pgaId = hit.pga_id || hit.legacy_id;
      const nextSelection: ActiveCallSelection = {
        callSid: selection.callSid,
        personId: hit.objectID,
        pgaId,
        ...(selection.ended ? { ended: true } : {})
      };

      storeActiveCallSelection(nextSelection);
      setSelection(nextSelection);
      setIsChangingPerson(false);
      setIsOpen(true);
      shouldFocusNotesAfterSelectionRef.current = true;
    }
  });

  useEffect(() => {
    if (!enabled) {
      setSelection(null);
      setIsOpen(false);
      return;
    }

    const refreshSelection = () => {
      const nextSelection = readActiveCallSelection();
      setSelection(nextSelection);
      if (!nextSelection) setIsOpen(false);
      if (nextSelection?.ended) setIsOpen(true);
    };

    refreshSelection();
    window.addEventListener(ACTIVE_CALL_SESSION_CHANGED_EVENT, refreshSelection);
    window.addEventListener("storage", refreshSelection);

    return () => {
      window.removeEventListener(ACTIVE_CALL_SESSION_CHANGED_EVENT, refreshSelection);
      window.removeEventListener("storage", refreshSelection);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !selection) return;

    const requestedCallSid = (location.state as { openActiveCallPanel?: string } | null)?.openActiveCallPanel;
    if (!requestedCallSid || requestedCallSid !== selection.callSid || selection.ended) return;

    setIsOpen(true);
    shouldFocusNotesAfterSelectionRef.current = true;
    navigate(`${location.pathname}${location.search}${location.hash}` as Path, {
      replace: true,
      state: null
    });
  }, [enabled, location, navigate, selection]);

  useEffect(() => {
    if (!selection?.callSid || !interactionId) return;
    if (polledCallSid !== selection.callSid) return;

    clearActiveCallSelectionForCallSid(selection.callSid);
  }, [interactionId, polledCallSid, selection?.callSid]);

  useEffect(() => {
    if (!selection) {
      hydratedCallSidRef.current = undefined;
      appliedTopicCallSidRef.current = undefined;
      setNotes("");
      setTopicOverride(null);
      setSubtopicOverride(null);
      setTopicTouched(false);
      setTopicQuery("");
      resetTask();
      return;
    }

    if (hydratedCallSidRef.current === selection.callSid) return;

    const draft = readActiveCallDraft();
    const matchingDraft = draft?.callSid === selection.callSid ? draft : null;

    hydratedCallSidRef.current = selection.callSid;
    appliedTopicCallSidRef.current = undefined;
    setNotes(matchingDraft?.notes || "");
    setTopicOverride(matchingDraft?.topicOverride ?? null);
    setSubtopicOverride(matchingDraft?.subtopicOverride ?? null);
    setTopicTouched(!!matchingDraft?.topicTouched);
    setTopicQuery(matchingDraft?.topicQuery || "");

    if (matchingDraft?.attachTask) {
      toggleAttachTask(true, matchingDraft.taskName || getDefaultTaskName(selectedPersonName));
      setTaskName(matchingDraft.taskName || getDefaultTaskName(selectedPersonName));
      setTaskDueDate(isoDateToDateValue(matchingDraft.taskDueDate));
      setTaskIsPrivate(!!matchingDraft.taskIsPrivate);
      if (matchingDraft.taskAssignee === null) {
        clearAssignee();
      } else if (matchingDraft.taskAssignee) {
        selectAssignee(matchingDraft.taskAssignee);
      }
    } else {
      resetTask();
    }
  }, [
    clearAssignee,
    resetTask,
    selectAssignee,
    selection,
    selectedPersonName,
    setTaskDueDate,
    setTaskIsPrivate,
    setTaskName,
    toggleAttachTask
  ]);

  useEffect(() => {
    setIsChangingPerson(false);
  }, [selection?.callSid, selection?.pgaId]);

  // Pre-fill overrides from AI classification once per call. Never clobbers rep edits,
  // including a touched override restored from the wrap-up draft (state may not have
  // hydrated yet when this effect first runs, so check the draft directly).
  useEffect(() => {
    if (!selection || topicTouched) return;
    if (transcriptSummaryStatus !== "completed") return;
    if (appliedTopicCallSidRef.current === selection.callSid) return;
    const draft = readActiveCallDraft();
    if (draft?.callSid === selection.callSid && draft.topicTouched) return;

    appliedTopicCallSidRef.current = selection.callSid;
    setTopicOverride(transcriptTopic ?? "Other");
    setSubtopicOverride(transcriptSubtopic ?? "Other");
    setTopicQuery(topicPairLabel(transcriptTopic, transcriptSubtopic));
  }, [selection, topicTouched, transcriptSummaryStatus, transcriptTopic, transcriptSubtopic]);

  useEffect(() => {
    if (!selection || hydratedCallSidRef.current !== selection.callSid) return;

    const draft: ActiveCallDraft = {
      callSid: selection.callSid,
      notes,
      topicOverride,
      subtopicOverride,
      topicTouched,
      topicQuery,
      attachTask: showAttachTask ? attachTask : false,
      taskName: showAttachTask ? taskName : undefined,
      taskAssignee: showAttachTask && attachTask ? taskAssignee : undefined,
      taskDueDate: showAttachTask ? dateValueToISODate(taskDueDate) : undefined,
      taskIsPrivate: showAttachTask ? taskIsPrivate : undefined
    };

    storeActiveCallDraft(draft);
  }, [
    attachTask,
    notes,
    selection,
    showAttachTask,
    taskAssignee,
    taskDueDate,
    taskIsPrivate,
    taskName,
    topicOverride,
    subtopicOverride,
    topicTouched,
    topicQuery
  ]);

  useEffect(() => {
    if (!shouldFocusNotesAfterSelectionRef.current || !isOpen || !hasSelectedPerson || showPersonSearch) return;

    const timeoutId = window.setTimeout(() => {
      notesInputRef.current?.focus();
      shouldFocusNotesAfterSelectionRef.current = false;
    });

    return () => window.clearTimeout(timeoutId);
  }, [hasSelectedPerson, isOpen, selection?.pgaId, showPersonSearch]);

  useEffect(() => {
    if (!shouldFocusSearchAfterClearRef.current || !isOpen || hasSelectedPerson || !showPersonSearch) return;

    focusInput();
    shouldFocusSearchAfterClearRef.current = false;
  }, [focusInput, hasSelectedPerson, isOpen, showPersonSearch]);

  const selectedPersonSupportingText = useMemo(() => {
    if (!selection?.pgaId) return undefined;
    return `PGA ID: ${selection.pgaId}`;
  }, [selection?.pgaId]);

  if (!enabled || !selection) return null;

  const defaultTaskName = getDefaultTaskName(selectedPersonName);
  const personSearchTrailingIcon =
    displayValue || query ? (
      <button className="cursor-pointer flex items-center h-full" onClick={handleClear} aria-label="Clear search query">
        <Icon name="cancel" />
      </button>
    ) : undefined;

  const clearSelectedPerson = () => {
    const nextSelection: ActiveCallSelection = {
      callSid: selection.callSid,
      ...(selection.ended ? { ended: true } : {})
    };
    handleClear();
    storeActiveCallSelection(nextSelection);
    setSelection(nextSelection);
    setIsChangingPerson(true);
    shouldFocusSearchAfterClearRef.current = true;
  };

  const clearCurrentCallSession = () => {
    clearActiveCallSelectionForCallSid(selection.callSid);
    resetTask();
    setNotes("");
    setFinalizeError(undefined);
    setIsChangingPerson(false);
  };

  const handleSidebarOpenChange = (open: boolean) => {
    if (isCallEnded && !open) return;
    setIsOpen(open);
  };

  // Interaction categories come from the same staff-confirmed Flex topic shown
  // in the wrap-up UI — no second AI guess. Untouched + AI-pending finalizes
  // as "Other" (always in the catalog); the late AI classification still lands
  // on the call row via the backend backfill. The catalog guard covers drift
  // between the Flex taxonomy and the interaction-topics catalog.
  const buildFinalizeCategories = (): string[] => {
    if (validCategoryNames.includes(selectedTopic)) return [selectedTopic];
    if (validCategoryNames.includes("Other")) return ["Other"];
    return validCategoryNames[0] ? [validCategoryNames[0]] : ["Other"];
  };

  const applyTopicOverride = (topic: string, subtopic: string) => {
    setTopicTouched(true);
    setTopicOverride(topic);
    setSubtopicOverride(subtopic);
    setTopicQuery(topicPairLabel(topic, subtopic));
  };

  const handleTopicReset = () => {
    // Untouched + AI pending sends no topic args, so restore the empty input
    // (placeholder + supporting text) rather than an Other/Other label.
    setTopicQuery(aiTopicPending ? "" : topicPairLabel(transcriptTopic, transcriptSubtopic));
    setTopicOverride(null);
    setSubtopicOverride(null);
    setTopicTouched(false);
  };

  // Discard unselected free text so the input matches the finalize payload.
  // Reads latest state from a ref: Autocomplete delays onBlur 200ms, and a
  // chip or reset click in that window would otherwise be display-reverted.
  const handleTopicBlur = () => {
    const { touched, pending, topic, subtopic } = topicBlurStateRef.current;
    if (!touched && pending) {
      setTopicQuery("");
      return;
    }
    setTopicQuery(topicPairLabel(topic, subtopic));
  };

  const finalizeInteraction = async () => {
    if (!canFinalizeInteraction) {
      return;
    }

    // Clear unselected filter text so the field matches the finalize payload.
    handleTopicBlur();

    if (attachTask && !taskHook.validate()) return;

    // Failed and unavailable transcripts never backfill, so the rep's notes are
    // the only record — require them. Pending/processing may finalize empty.
    if ((transcriptSummaryStatus === "failed" || transcriptSummaryStatus === "unavailable") && !notes.trim()) {
      setFinalizeError("Call details are required.");
      notesInputRef.current?.focus();
      return;
    }

    const participantId = selectedPerson?.id || selection.personId;
    if (!participantId) {
      setFinalizeError(
        "We couldn't find the selected person. Discard these call details or select a different person."
      );
      return;
    }

    setFinalizeError(undefined);
    const taskInput = showAttachTask ? taskHook.getTaskInput(defaultTaskName) : null;
    const categories = buildFinalizeCategories();

    try {
      const { data } = await createInteraction({
        variables: {
          input: {
            participantId,
            interactionDate: new Date().toISOString().split("T")[0],
            mode: "Phone Call",
            twilioFlexCallSid: selection.callSid,
            ...finalizeTopicArgs,
            categories,
            description: getFinalizeDescription(notes) ?? "",
            summary: null,
            privacyRestricted: false,
            ...(taskInput ? { task: taskInput } : {})
          }
        }
      });

      if (data?.createInteraction?.success) {
        const interactionId = data.createInteraction.interaction?.id;
        const taskId = data.createInteraction.task?.id;

        showSnackbar("Interaction finalized", {
          ...(interactionId &&
            selection.pgaId && {
              actions: [
                <Button
                  key="view-interaction"
                  variant="text"
                  onClick={() =>
                    navigate(`/people/${selection.pgaId}/interactions/interactions/${interactionId}` as Path)
                  }
                >
                  View Interaction
                </Button>
              ]
            })
        });
        if (taskInput && taskId) {
          showSnackbar("Task created", {
            actions: [
              <Button key="view-task" variant="text" onClick={() => navigate(`/tasks/${taskId}` as Path)}>
                View task
              </Button>
            ]
          });
        }
        trackEvent(INTERACTION_ANALYTICS_EVENTS.created, {
          source: INTERACTION_CREATE_SOURCES.incomingCall,
          audience: "single",
          mode: "Phone Call",
          activityType: categories,
          privacyRestricted: false,
          participants: [participantId],
          interactionId,
          pgaId: selection.pgaId,
          success: true
        });
        refetchPersonInteractionViews(apolloClient);
        clearCurrentCallSession();
      } else {
        setFinalizeError(data?.createInteraction?.message || FINALIZE_INTERACTION_ERROR_MESSAGE);
      }
    } catch {
      setFinalizeError(FINALIZE_INTERACTION_ERROR_MESSAGE);
    }
  };

  return (
    <SideSheetModal
      isSidebarOpen={isOpen}
      setSidebarOpen={handleSidebarOpenChange}
      title={isCallEnded ? "Finalize Call Details" : "Call Details"}
      persistentTabLabel={getTabLabel(selectedPersonName)}
      persistentTabStatus={isCallEnded ? "inactive" : "active"}
      disableClose={isCallEnded}
      disableFocusManagement={!showPersonSearch}
      actions={
        isCallEnded ? (
          <div className="flex justify-end space-x-4">
            <Button
              variant="filled"
              icon={finalizingInteraction ? <Progress size={20} color="currentColor" /> : undefined}
              onClick={finalizeInteraction}
              disabled={finalizingInteraction || !canFinalizeInteraction}
            >
              {finalizingInteraction ? "Finalizing..." : "Finalize Interaction"}
            </Button>
            <Button
              variant="text"
              theme="error"
              icon="delete"
              onClick={clearCurrentCallSession}
              disabled={finalizingInteraction}
            >
              Discard
            </Button>
          </div>
        ) : (
          <Button variant="filled" onClick={() => setIsOpen(false)}>
            Save
          </Button>
        )
      }
    >
      <div className={`flex flex-col gap-6 ${showPersonSearch && !hasSelectedPerson ? "" : "pt-4"}`}>
        {finalizeError && (
          <Banner variant="error" description={finalizeError} inline closable={false} hideSecondaryDismiss />
        )}

        <section>
          {hasSelectedPerson && (
            <SubSectionHeading className="mb-3">
              {isCallEnded ? "Call Completed With" : "In Conversation With"}
            </SubSectionHeading>
          )}

          {!showPersonSearch && hasSelectedPerson && (
            <div className="rounded-lg border border-sys-brand-outline p-3">
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <ListItem
                    compact
                    useAvatar
                    picture={selectedPerson?.profilePhoto || undefined}
                    headline={selectedPerson?.displayName || "Selected person"}
                    supportingText={selectedPersonSupportingText}
                  />
                </div>
                <button
                  type="button"
                  className="text-sys-brand-on-surface-variant"
                  aria-label="Clear selected person"
                  onClick={clearSelectedPerson}
                >
                  <Icon name="cancel" />
                </button>
              </div>
            </div>
          )}

          {showPersonSearch && (
            <div className="space-y-4">
              <div
                className={`sticky top-0 z-10 bg-sys-brand-surface-container-low ${isCallEnded ? "pt-6" : "pt-4"}`}
                data-algolia-sticky-search-header="true"
                ref={searchBoxRef}
              >
                <TextField
                  label={isCallEnded ? "Call Completed With" : "In Conversation With"}
                  placeholder="Search for a person"
                  value={displayValue || query}
                  onChangeText={(value) => {
                    setDisplayValue(undefined);
                    handleQueryChange(value);
                  }}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  leadingIcon="search"
                  trailingIcon={personSearchTrailingIcon}
                  variant="outlined"
                  className="w-full"
                />
              </div>
              {query.trim() && (
                <AlgoliaSearchResults
                  results={results}
                  query={query}
                  isLoading={isLoading}
                  highlightedIndex={highlightedIndex}
                  setHighlightedIndex={setHighlightedIndex}
                  handleItemSelected={handleItemSelected}
                  itemRefs={itemRefs}
                  resultsContainerRef={resultsContainerRef}
                  renderHit={renderCreateInteractionPersonHit}
                  noResultsMessage={personSearchFailed ? "Couldn't load people" : "No results found"}
                />
              )}
            </div>
          )}
        </section>

        {transcriptStatusLineItem && (
          <ProcessStepLineItem
            bordered
            variant="subtle"
            testId={isCallEnded ? "call-transcript-summary-status" : "call-transcript-recording-status"}
            status={transcriptStatusLineItem.status}
            headline={transcriptStatusLineItem.headline}
            supportingText={transcriptStatusLineItem.supportingText}
          />
        )}

        {hasSelectedPerson && (
          <>
            <TextField
              ref={notesInputRef}
              label="Call Note"
              value={notes}
              onChangeText={(value) => {
                setNotes(value);
                setFinalizeError(undefined);
              }}
              type="textarea"
              rows={8}
              required={transcriptSummaryStatus === "failed" || transcriptSummaryStatus === "unavailable"}
              error={isCallEnded && finalizeError === "Call details are required."}
              errorText={isCallEnded && finalizeError === "Call details are required." ? finalizeError : undefined}
              variant="outlined"
              className="w-full"
            />

            {isCallEnded && taxonomyLoading && taxonomy.length === 0 && (
              <SkeletonElement height="h-14" width="w-full" data-testid="call-topic-taxonomy-skeleton" />
            )}

            {isCallEnded && taxonomyFailed && taxonomy.length === 0 && (
              <Banner
                variant="error"
                description={LOAD_INTERACTION_TOPICS_ERROR_MESSAGE}
                inline
                closable={false}
                hideSecondaryDismiss
              />
            )}

            {isCallEnded && taxonomy.length > 0 && (
              <>
                <Autocomplete
                  label="Call Topic"
                  data={filteredTopicOptions}
                  query={topicQuery}
                  onChangeText={setTopicQuery}
                  reset={handleTopicReset}
                  onBlur={handleTopicBlur}
                  onSelect={(option: TopicOption) => applyTopicOverride(option.topic, option.subtopic)}
                  renderItem={(option: TopicOption) => <span>{option.label}</span>}
                  variant="searchableDropdown"
                  leadingIcon="search"
                  supportingText={`AI selected: ${aiTopicLabel} › ${aiSubtopicLabel}`}
                  placeholder="Search topics"
                  data-testid="call-topic-autocomplete"
                />
                {visibleRunnersUp.length > 0 && (
                  <div className="flex flex-col gap-2" data-testid="call-topic-runners-up">
                    <p className="text-body-small text-sys-brand-on-surface-variant m-0">Other likely topics</p>
                    <div role="group" aria-label="Other likely topics">
                      <ChipSet>
                        {visibleRunnersUp.map((runnerUp) => {
                          const subtopic = runnerUp.subtopic ?? "Other";
                          return (
                            <Chip
                              key={topicOptionLabel(runnerUp.topic, subtopic)}
                              label={topicOptionLabel(runnerUp.topic, subtopic)}
                              selected={selectedTopic === runnerUp.topic && selectedSubtopic === subtopic}
                              showCheckmark={false}
                              onClick={() => applyTopicOverride(runnerUp.topic, subtopic)}
                            />
                          );
                        })}
                      </ChipSet>
                    </div>
                  </div>
                )}
              </>
            )}

            {showAttachTask && (
              <AttachTaskSection
                attachTask={attachTask}
                onToggle={(enabled) => toggleAttachTask(enabled, defaultTaskName)}
                taskName={taskName}
                onTaskNameChange={setTaskName}
                taskAssignee={taskAssignee}
                assigneeSearchQuery={taskAssigneeSearch}
                onAssigneeSearchChange={setTaskAssigneeSearch}
                selectAssignee={selectAssignee}
                clearAssignee={clearAssignee}
                assigneeResults={usersData}
                assigneeLoading={usersLoading}
                taskDueDate={taskDueDate}
                onDueDateChange={setTaskDueDate}
                taskIsPrivate={taskIsPrivate}
                onTaskIsPrivateChange={setTaskIsPrivate}
                errors={taskErrors}
                clearError={clearError}
                objectLabel="interaction"
                selectedTeamTags={taskHook.selectedTeamTags}
                teamSearchTerm={taskHook.teamSearchTerm}
                onTeamSearchChange={taskHook.setTeamSearchTerm}
                availableDepartmentOptions={taskHook.availableDepartmentOptions}
                onTeamVisibilitySelect={taskHook.handleTeamVisibilitySelect}
                onRemoveTeamTag={taskHook.removeTeamTag}
              />
            )}
          </>
        )}
      </div>
    </SideSheetModal>
  );
}
